import type { KeyboardEvent } from "react";
import dayjs from "dayjs";
import { formatDuration } from "../evidenceActivity/formatDuration";

export interface BucketCount {
  total: number;
  unassigned: number;
  within24Hours?: number;
  nextDueAt?: string | null;
}

export interface DisputeSummary {
  overdue: BucketCount;
  dueIn3Days: BucketCount;
  dueIn4To7Days: BucketCount;
}

export type DisputeBucket = keyof DisputeSummary;

export interface BucketSelection {
  bucket: DisputeBucket;
  unassignedOnly: boolean;
}

type Tone = "critical" | "warning" | "neutral";

const CARDS: { key: DisputeBucket; label: string; hint: string; tone: Tone }[] = [
  { key: "overdue", label: "Overdue", hint: "Deadline passed, nothing sent", tone: "critical" },
  { key: "dueIn3Days", label: "Due in 3 days", hint: "Still open", tone: "warning" },
  { key: "dueIn4To7Days", label: "Due in 4–7 days", hint: "Still open", tone: "neutral" },
];

const VALUE_TONE: Record<Tone, string> = {
  critical: "text-[#d92d20]",
  warning: "text-[#b54708]",
  neutral: "text-gray-800 dark:text-white/90",
};

const SELECTED_RING: Record<Tone, string> = {
  critical: "border-[#d92d20] ring-1 ring-[#d92d20]",
  warning: "border-[#b54708] ring-1 ring-[#b54708]",
  neutral: "border-brand-500 ring-1 ring-brand-500",
};

const DOT_TONE: Record<Tone, string> = {
  critical: "bg-[#d92d20]",
  warning: "bg-[#b54708]",
  neutral: "bg-brand-500",
};

const timeLeft = (seconds: number) =>
  seconds >= 86400 ? `${Math.floor(seconds / 86400)}d ${Math.floor((seconds % 86400) / 3600)}h` : formatDuration(seconds);

export default function DisputeSummaryCards({
  summary,
  serverNow,
  selected,
  onSelect,
}: {
  summary: DisputeSummary;
  serverNow?: string;
  selected: BucketSelection | null;
  onSelect: (selection: BucketSelection) => void;
}) {
  return (
    <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
      {CARDS.map((c) => {
        const isSelected = selected?.bucket === c.key;
        const isUnassignedSelected = isSelected && selected.unassignedOnly;
        const { total, unassigned, within24Hours, nextDueAt } = summary[c.key];
        const next = nextDueAt ? timeLeft(dayjs(nextDueAt).diff(serverNow ? dayjs(serverNow) : dayjs(), "second")) : null;
        const hint = !next ? c.hint : within24Hours ? `${within24Hours} within 24h · next in ${next}` : `Next in ${next}`;
        const selectCard = () => onSelect({ bucket: c.key, unassignedOnly: false });
        const onCardKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
          if (e.target !== e.currentTarget || (e.key !== "Enter" && e.key !== " ")) return;
          e.preventDefault();
          selectCard();
        };
        return (
          <div
            key={c.key}
            role="button"
            tabIndex={0}
            aria-pressed={isSelected && !isUnassignedSelected}
            onClick={selectCard}
            onKeyDown={onCardKeyDown}
            className={`cursor-pointer rounded-2xl border bg-white p-5 text-left transition hover:border-gray-300 hover:shadow-sm dark:bg-white/[0.03] dark:hover:border-gray-700 ${
              isSelected ? SELECTED_RING[c.tone] : "border-gray-200 dark:border-gray-800"
            }`}
          >
            <div className="flex items-center justify-between gap-2">
              <div className="text-sm font-medium text-gray-500 dark:text-gray-400">{c.label}</div>
              {isSelected && (
                <span
                  className="flex size-5 shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs text-gray-500 dark:bg-white/10 dark:text-gray-300"
                  title="Clear filter"
                  aria-hidden
                >
                  ✕
                </span>
              )}
            </div>
            <div className={`mt-2 text-3xl font-bold tabular-nums ${VALUE_TONE[c.tone]}`}>{total}</div>
            <div className="mt-1 text-xs text-gray-400 dark:text-gray-500">{hint}</div>
            {unassigned > 0 && (
              <button
                type="button"
                aria-pressed={isUnassignedSelected}
                onClick={(e) => {
                  e.stopPropagation();
                  onSelect({ bucket: c.key, unassignedOnly: true });
                }}
                className={`mt-3 inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium transition ${
                  isUnassignedSelected
                    ? "bg-gray-200 text-gray-900 dark:bg-white/15 dark:text-white"
                    : "text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-white/10"
                }`}
              >
                <span className={`size-1.5 rounded-full ${DOT_TONE[c.tone]}`} aria-hidden />
                <span><span className="tabular-nums">{unassigned}</span> unassigned</span>
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}
